import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DatePicker } from "@/components/ui/date-picker"
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Section as SectionReport } from '@/pages/order/report/ReportForm';

import { Card } from "@/components/ui/card"

import { debounce, intToChar } from "@/lib/utils"
import { CircleXIcon, CornerDownRightIcon, GripVerticalIcon, LoaderCircleIcon, MoveVerticalIcon, PlusCircleIcon, SparklesIcon, Trash2Icon } from "lucide-react"
import { useOrder } from "../../../contexts/OrderContext"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Textarea } from "@/components/ui/textarea"
import React from "react"

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DividerHorizontalIcon, DragHandleVerticalIcon } from "@radix-ui/react-icons";
import { Switch } from "@/components/ui/switch";
import { useAudio } from "@/contexts/AudioContext";
import clsx from "clsx";
import cournoyer from "@/cournoyer";
import { supabase } from "@/lib/supabase";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";


const { APP_API_URL } = import.meta.env

export default function MandateForm({ disabled, containerRef }) {
    const { compareOrder, order, setOrder } = useOrder()
    const [loadingHtml, setLoadingHtml] = React.useState(false)
    const { session } = useAuth()

    const debouncedSetMandateReviewed = React.useCallback(
        debounce((jsonMandateReviewed) => {
            if (jsonMandateReviewed) {
                supabase.from('folders').update({ jsonMandateReviewed }).eq('pydio_id', order.pydio_id).then()
            }
        }, 500),
        []
    );

    const update = (value, keys) => {
        if (disabled) {
            return;
        }

        setOrder((prevOrder) => {
            const nextOrder = {
                ...prevOrder,
                mandate: {
                    ...prevOrder.mandate,
                    form: {
                        ...prevOrder.mandate.form,
                        [keys[0]]: {
                            ...prevOrder.mandate.form[keys[0]],
                            ...(keys.length === 2 ?
                                { [keys[1]]: value } :
                                { [keys[1]]: { ...prevOrder.mandate.form[keys[1]], [keys[2]]: value } }
                            )
                        }
                    }
                }
            }

            if (nextOrder.mandate.reviewed) {
                debouncedSetMandateReviewed(nextOrder.mandate.form);
            }

            return nextOrder
        })
    }

    const getBorderClass = (value1, value2) => {
        if (value1 === value2) {
            return "border border-green-500";
        } else if (value1 !== value2) {
            return "border border-red-500";
        }
        return ""
    };

    const generateMandateHtml = async () => {
        setLoadingHtml(true)
        await supabase.from('folders').update({ jsonMandateReviewed: order.mandate.form }).eq('pydio_id', order.pydio_id)
        
        const { data } = await fetch(`${APP_API_URL}/generation/section/${order.pydio_id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                sections: ["mandate_static"]
            }),
        }).then(res => res.json())
        
        setLoadingHtml(false)

        setOrder({
            ...order,
            sections: {
                ...order.sections,
                ...data
            }
        })
    }

    return <>
        <div className="flex flex-col gap-4 p-6">
            <div className="flex gap-4">
                <Section title="Mandant">
                    {!compareOrder && <Input readOnly={disabled} value={order.mandate.form.mandataryCompany.name} onChange={(e) => update(e.target.value, ['mandataryCompany', 'name'])} />}
                    {compareOrder && <>
                        <Input className={getBorderClass(
                            order.mandate.form.mandataryCompany.name,
                            order.mandate.form_generated.mandataryCompany.name
                        )} readOnly={true} value={order.mandate.form_generated.mandataryCompany.name} />
                        <Input readOnly={true} value={order.mandate.form.mandataryCompany.name} />
                    </>}
                </Section>

                <Section title="&nbsp;">
                    {!compareOrder && <div className="flex gap-2">
                        <Select value={order.mandate.form.mandataryCompany.gender} onValueChange={(value) => update(value, ['mandataryCompany', 'gender'])}>
                            <SelectTrigger className="w-[160px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem value="M">Monsieur</SelectItem>
                                    <SelectItem value="F">Madame</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>

                        <Input readOnly={disabled} value={order.mandate.form.mandataryCompany.appellation} onChange={(e) => update(e.target.value, ['mandataryCompany', 'appellation'])} />
                    </div>}
                    {compareOrder && <>
                        <div className="flex gap-2">
                            <Select value={order.mandate.form_generated.mandataryCompany.gender}>
                                <SelectTrigger className={clsx("w-[160px]",
                                    getBorderClass(
                                        order.mandate.form.mandataryCompany.gender,
                                        order.mandate.form_generated.mandataryCompany.gender
                                    ))}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="M">Monsieur</SelectItem>
                                        <SelectItem value="F">Madame</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>

                            <Input className={getBorderClass(
                                order.mandate.form.mandataryCompany.appellation,
                                order.mandate.form_generated.mandataryCompany.appellation
                            )} readOnly={true} value={order.mandate.form_generated.mandataryCompany.appellation} />
                        </div>

                        <div className="flex gap-2">
                            <Select value={order.mandate.form.mandataryCompany.gender}>
                                <SelectTrigger className="w-[160px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="M">Monsieur</SelectItem>
                                        <SelectItem value="F">Madame</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>

                            <Input readOnly={disabled} value={order.mandate.form.mandataryCompany.appellation} />
                        </div>
                    </>}
                </Section>
            </div>

            <div className="flex gap-4">
                <Section title="Employé">
                    {!compareOrder && <div className="flex gap-2">
                        <Select value={order.mandate.form.patient.gender} onValueChange={(value) => update(value, ['patient', 'gender'])}>
                            <SelectTrigger className="w-[160px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem value="M">Monsieur</SelectItem>
                                    <SelectItem value="F">Madame</SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>

                        <Input readOnly={disabled} value={order.mandate.form.patient.firstName} placeholder="Prénom" onChange={(e) => update(e.target.value, ['patient', 'firstName'])} />
                        <Input readOnly={disabled} value={order.mandate.form.patient.lastName} placeholder="Nom" onChange={(e) => update(e.target.value, ['patient', 'lastName'])} />
                    </div>}

                    {compareOrder && <>
                        <div className="flex gap-2">
                            <Select value={order.mandate.form_generated.patient.gender}>
                                <SelectTrigger className={clsx("w-[160px]",
                                    getBorderClass(
                                        order.mandate.form.patient.gender,
                                        order.mandate.form_generated.patient.gender
                                    ))}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="M">Monsieur</SelectItem>
                                        <SelectItem value="F">Madame</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>

                            <Input readOnly={disabled} className={getBorderClass(
                                order.mandate.form.patient.firstName,
                                order.mandate.form_generated.patient.firstName
                            )} value={order.mandate.form_generated.patient.firstName} placeholder="Prénom" />
                            <Input readOnly={disabled} className={getBorderClass(
                                order.mandate.form.patient.lastName,
                                order.mandate.form_generated.patient.lastName
                            )} value={order.mandate.form_generated.patient.lastName} placeholder="Nom" />
                        </div>

                        <div className="flex gap-2">
                            <Select value={order.mandate.form.patient.gender}>
                                <SelectTrigger className="w-[160px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        <SelectItem value="M">Monsieur</SelectItem>
                                        <SelectItem value="F">Madame</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>

                            <Input readOnly={disabled} value={order.mandate.form.patient.firstName} placeholder="Prénom" />
                            <Input readOnly={disabled} value={order.mandate.form.patient.lastName} placeholder="Nom" />
                        </div>
                    </>}
                </Section>

                <Section title="Date de naissance">
                    {!compareOrder && <DateTimePicker hideTime value={order.mandate.form.patient.birthDate} onChange={(value) => update(value, ['patient', 'birthDate'])} />}
                    {compareOrder && <>
                        <DateTimePicker className={getBorderClass(
                            order.mandate.form.patient.birthDate,
                            order.mandate.form_generated.patient.birthDate
                        )} hideTime value={order.mandate.form_generated.patient.birthDate} />
                        <DateTimePicker hideTime value={order.mandate.form.patient.birthDate} />
                    </>}
                </Section>
            </div>

            <div className="flex gap-4">
                <Section title="Employeur">
                    {!compareOrder && <Input readOnly={disabled} value={order.mandate.form.patient.company.name} onChange={(e) => update(e.target.value, ['patient', 'company', 'name'])} />}
                    {compareOrder && <>
                        <Input readOnly={disabled} className={getBorderClass(
                            order.mandate.form.patient.company.name,
                            order.mandate.form_generated.patient.company.name
                        )} value={order.mandate.form_generated.patient.company.name} />
                        <Input readOnly={disabled} value={order.mandate.form.patient.company.name} />
                    </>}
                </Section>
                <Section title={order.mandate.form.isCNESST ? "Date de l'événement" : "Date de l'arrêt de travail"}>
                    {!compareOrder && <DateTimePicker hideTime value={order.mandate.form.patient.stopageWorkDate} onChange={(value) => update(value, ['patient', 'stopageWorkDate'])} />}
                    {compareOrder && <>
                        <DateTimePicker className={getBorderClass(
                            order.mandate.form.patient.company.stopageWorkDate,
                            order.mandate.form_generated.patient.company.stopageWorkDate
                        )} hideTime value={order.mandate.form_generated.patient.stopageWorkDate} />
                        <DateTimePicker hideTime value={order.mandate.form.patient.stopageWorkDate} />
                    </>}
                </Section>
            </div>
        </div >

        <Separator />

        {!order.sections.mandate_static && <div className="flex flex-col gap-4 p-6">
            <Section title="Questions">
                <MandateQuestions disabled={disabled} questions={order.mandate.form.questions} />
            </Section>

            <div className="flex items-center space-x-2">
                <Switch checked={order.mandate.form.isCNESST} id="CNESST" onCheckedChange={(value) => {
                    if (disabled) {
                        return;
                    }

                    setOrder((prevOrder) => ({
                        ...prevOrder,
                        mandate: {
                            ...prevOrder.mandate,
                            form: {
                                ...prevOrder.mandate.form,
                                isCNESST: value
                            }
                        }
                    }))
                }} />
                <Label htmlFor="CNESST">CNESST</Label>
            </div>

            {order.mandate.form.isCNESST && <>
                <Section title="Diagnostics acceptés">
                    <MandateDiagnosticsList disabled={disabled} />
                </Section>

                <Section title="Informations suplémentaire CNESST">
                    <AutoResizingTextarea readOnly={disabled} value={order.mandate.form.diagnostics.other} onChange={(e) => update(e.target.value, ['diagnostics', 'other'])} />
                </Section>
            </>}

            {!order.isLayout && <Button className="w-full" disabled={loadingHtml} size={"sm"} onClick={generateMandateHtml}>
                {loadingHtml && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                {!loadingHtml && <SparklesIcon className="mr-2 w-4 h-4" />}
                {"Générer la section mandat du raport"}
            </Button>}
        </div>}

        {!!order.sections.mandate_static && <div className="p-6"><SectionReport section={{ value: 'mandate_static', label: 'MANDAT', transcription_key: false, hideHeader: true }} /></div>}
        {/* {<div className="p-6"><SectionReport section={{ value: 'mandate_static', label: 'MANDAT', transcription_key: false, hideHeader: true }} /></div>} */}
    </>
}

function Section({ title, children }) {
    return <div className="flex flex-col w-full gap-2.5">
        <Label>
            {title}
        </Label>
        {children}
    </div>
}

function MandateDiagnosticsList({ disabled }) {
    const { order, setOrder } = useOrder()

    const create = () => {
        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    diagnostics: {
                        ...order.mandate.form.diagnostics,
                        list: [...order.mandate.form.diagnostics.list, ""]
                    }
                }
            }
        })
    }

    const update = (index, value) => {
        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    diagnostics: {
                        ...order.mandate.form.diagnostics,
                        list: order.mandate.form.diagnostics.list.map((diagnostic, i) => i === index ? value : diagnostic)
                    }
                }
            }
        });
    };

    const remove = (index) => {
        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    diagnostics: {
                        ...order.mandate.form.diagnostics,
                        list: order.mandate.form.diagnostics.list.filter((_, i) => i !== index)
                    }
                }
            }
        })
    }

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedDiagnostics = Array.from(order.mandate.form.diagnostics.list);
        const [removed] = reorderedDiagnostics.splice(result.source.index, 1);
        reorderedDiagnostics.splice(result.destination.index, 0, removed);

        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    diagnostics: {
                        ...order.mandate.form.diagnostics,
                        list: reorderedDiagnostics
                    }
                }
            }
        });
    };

    return <div className="flex flex-col gap-4">
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="diagnostics">
                {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-col">
                        {order.mandate.form.diagnostics.list.map((diagnostic, i) => (
                            <Draggable key={i} draggableId={i.toString()} index={i}>
                                {(provided) => (
                                    <div
                                        className="mb-2 flex items-center gap-2"
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                    >
                                        {/* <GripVerticalIcon className="cursor-move" /> */}
                                        <Input readOnly={disabled} onChange={(e) => update(i, e.target.value)} value={diagnostic} />
                                        {!disabled && <Button
                                            size={"sm"}
                                            variant="outline"
                                            onClick={(e) => remove(i)}
                                        >
                                            <Trash2Icon />
                                        </Button>}
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>

        {!disabled && <div className="flex justify-end">
            <Button
                size={"sm"}
                variant="outline"
                onClick={create}
            >
                <PlusCircleIcon className="w-4 h-4 mr-2" />
                Ajouter un diagnostic
            </Button>
        </div>}
    </div>
}

function MandateQuestions({ questions, parentIndex, disabled }) {
    const { order, setOrder } = useOrder()

    const create = (index) => {
        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    questions: [...order.mandate.form.questions, {
                        question: "",
                        questions: []
                    }]
                }
            }
        })
    }

    const onDragEnd = (result) => {
        if (!result.destination) return;

        if (typeof parentIndex === 'undefined') {
            const reorderedQuestions = Array.from(order.mandate.form.questions);
            const [removed] = reorderedQuestions.splice(result.source.index, 1);
            reorderedQuestions.splice(result.destination.index, 0, removed);

            setOrder({
                ...order,
                mandate: {
                    ...order.mandate,
                    form: {
                        ...order.mandate.form,
                        questions: reorderedQuestions
                    }
                }
            });
        } else {
            const reorderedSubQuestions = Array.from(order.mandate.form.questions[parentIndex].questions);
            const [removed] = reorderedSubQuestions.splice(result.source.index, 1);
            reorderedSubQuestions.splice(result.destination.index, 0, removed);

            setOrder({
                ...order,
                mandate: {
                    ...order.mandate,
                    form: {
                        ...order.mandate.form,
                        questions: order.mandate.form.questions.map((q, i) => i === parentIndex ? {
                            ...q,
                            questions: reorderedSubQuestions
                        } : q)
                    }
                }
            });
        }
    };

    return <div className="flex flex-col gap-4">
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="questions">
                {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-col">
                        {questions.map((question, i) => (
                            <Draggable key={i} draggableId={i.toString()} index={i}>
                                {(provided) => (
                                    <div
                                        className="mb-2"
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                    >
                                        <MandateQuestion disabled={disabled} parentIndex={parentIndex} key={i} index={i} question={question} provided={provided} />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>

        {typeof parentIndex === 'undefined' && !disabled && <div className="flex justify-end">
            <Button
                size={"sm"}
                variant="outline"
                onClick={create}
            >
                <PlusCircleIcon className="w-4 h-4 mr-2" />
                Ajouter une question
            </Button>
        </div>}
    </div>
}

function MandateQuestion({ question, index, parentIndex, provided, disabled }) {
    const { order, setOrder } = useOrder()
    console.log(question)

    const create = (index) => {
        setOrder({
            ...order,
            mandate: {
                ...order.mandate,
                form: {
                    ...order.mandate.form,
                    questions: order.mandate.form.questions.map((q, i) => i === index ? {
                        ...q,
                        questions: q.questions.concat({
                            question: ""
                        })
                    } : q)
                }
            }
        })
    }

    const update = (value, keys) => setOrder({
        ...order,
        mandate: {
            ...order.mandate,
            form: {
                ...order.mandate.form,
                questions: order.mandate.form.questions.map((q, i) => i === keys[0] ? (keys.length === 2 ? {
                    ...q,
                    questions: q.questions.map((sq, si) => si === keys[1] ? {
                        ...sq,
                        question: value
                    } : sq)
                } : {
                    ...q,
                    question: value
                }) : q)
            }
        }
    })

    const remove = (keys) => {
        setOrder(() => {
            const newOrder = {
                ...order,
                mandate: {
                    ...order.mandate,
                    form: {
                        ...order.mandate.form,
                        questions: keys.length === 2 ? order.mandate.form.questions.map((q, i) => i === keys[0] ? {
                            ...q,
                            questions: q.questions.filter((sq, si) => si !== keys[1])
                        } : q) : order.mandate.form.questions.filter((q, i) => i !== keys[0])
                    }
                }
            }

            return newOrder
        })
    }



    return <div className="flex flex-col gap-2">
        <div className="flex gap-4 items-start group relative">
            <span className="text-muted-foreground">{typeof parentIndex === 'undefined' ? index + 1 : intToChar(index + 1)}.</span>
            <AutoResizingTextarea
                readOnly={disabled}
                value={question.question}
                onChange={(e) =>
                    update(e.target.value, typeof parentIndex === 'undefined' ? [index] : [parentIndex, index])
                }
            />

            {!disabled && <div className="absolute right-0 transform translate-y-[-50%] top-[-6px] hidden group-hover:flex gap-1">
                {<Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className="h-8 rounded-md px-3 text-xs border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                            {...provided.dragHandleProps}
                        >
                            <GripVerticalIcon />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Déplacer la question</p>
                    </TooltipContent>
                </Tooltip>}

                {typeof parentIndex === 'undefined' && <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            size={"sm"}
                            variant="outline"
                            onClick={(e) => create(index)}
                        >
                            <PlusCircleIcon />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Ajouter une question</p>
                    </TooltipContent>
                </Tooltip>}

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            size={"sm"}
                            variant="outline"
                            onClick={(e) => remove(typeof parentIndex === 'undefined' ? [index] : [parentIndex, index])}
                        >
                            <Trash2Icon />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Supprimer la question</p>
                    </TooltipContent>
                </Tooltip>
            </div>}
        </div>

        {!!question.questions?.length && <div className="pl-8 flex flex-col gap-2">
            <MandateQuestions disabled={disabled} questions={question.questions} parentIndex={index} />
        </div>}
    </div>
}

export function AutoResizingTextarea({ rows, value, onChange, readOnly, disabled, className, placeholder }) {
    const textareaRef = React.useRef(null);

    React.useEffect(() => {
        if (textareaRef.current) {
            // Reset and adjust height
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]); // Run effect when the value changes

    return (
        <Textarea
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            ref={textareaRef}
            value={value}
            onChange={onChange}
            style={{ height: 'auto', resize: 'none', overflow: 'hidden' }}
            onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            className={clsx("scrollbar-hide", className)}
        />
    );
}